import { useRef } from 'react';
import {
  ArrowRight,
  Circle,
  Frame as FrameIcon,
  Hand,
  Image as ImageIcon,
  MousePointer2,
  PenTool,
  RectangleHorizontal,
  Settings,
  Square,
  StickyNote,
  Type,
  type LucideIcon,
} from 'lucide-react';
import type { StickyColor } from '@frame/shared';
import type { ToolId } from './tools';
import { StickyColorPopover } from './StickyColorPopover';

interface ToolDef {
  id: ToolId;
  name: string;
  shortcut: string;
  icon: LucideIcon;
  disabled?: boolean;
  // When set, pressing the button can either start a click (if mouse is
  // released without moving) or start a drag-from-sidebar (if movement
  // exceeds threshold). Used by the sticky variants to support the Miro-
  // style "grab a sticky from the rail" gesture.
  draggable?: 'sticky-square' | 'sticky-wide';
}

const TOOL_GROUPS: ToolDef[][] = [
  [
    { id: 'select', name: 'Select', shortcut: 'V', icon: MousePointer2 },
    { id: 'hand', name: 'Hand', shortcut: 'H', icon: Hand },
  ],
  [
    {
      id: 'sticky',
      name: 'Sticky note (square)',
      shortcut: 'S',
      icon: StickyNote,
      draggable: 'sticky-square',
    },
    {
      id: 'sticky-wide',
      name: 'Sticky note (wide)',
      shortcut: '',
      icon: RectangleHorizontal,
      draggable: 'sticky-wide',
    },
    { id: 'text', name: 'Text', shortcut: 'T', icon: Type },
    { id: 'rect', name: 'Rectangle', shortcut: 'R', icon: Square },
    { id: 'ellipse', name: 'Ellipse', shortcut: 'O', icon: Circle },
    { id: 'arrow', name: 'Arrow', shortcut: 'A', icon: ArrowRight },
    { id: 'frame', name: 'Frame', shortcut: 'F', icon: FrameIcon },
    { id: 'pen', name: 'Pen', shortcut: 'P', icon: PenTool },
  ],
  [
    { id: 'image', name: 'Image', shortcut: 'I', icon: ImageIcon },
  ],
];

interface ToolbarProps {
  activeTool: ToolId;
  onSelectTool: (tool: ToolId) => void;
  onOpenSettings?: (anchor: DOMRect) => void;
  // Fired when the user begins dragging a sticky out of the rail. The handler
  // is responsible for tracking subsequent pointermove + pointerup events to
  // render a ghost and create the sticky on drop. `color` is undefined when
  // the drag started from one of the rail icons (caller picks default); set
  // explicitly when started from a swatch in the StickyColorPopover.
  onStartStickyDrag?: (
    variant: 'square' | 'wide',
    color: StickyColor | undefined,
    screenPos: { x: number; y: number },
  ) => void;
  // Active sticky color — drives the highlight + drop color in the popover.
  stickyColor: StickyColor;
  // Picker writes its choice back through this handler so the parent can
  // persist it (writeStickyColor) and reuse it on the next click-create.
  onPickStickyColor: (color: StickyColor) => void;
}

const DRAG_THRESHOLD_PX = 4;

export function Toolbar({
  activeTool,
  onSelectTool,
  onOpenSettings,
  onStartStickyDrag,
  stickyColor,
  onPickStickyColor,
}: ToolbarProps) {
  // Picker is visible whenever either sticky variant is the active tool.
  // Switching to any other tool (Esc, V, T, etc.) auto-closes it.
  const stickyVariant: 'square' | 'wide' = activeTool === 'sticky-wide' ? 'wide' : 'square';
  return (
    <aside
      className="
        relative flex flex-col items-center
        bg-bg-app border-r border-border-subtle
        py-2
      "
      aria-label="Tools"
    >
      <div className="flex flex-col gap-1 flex-1">
        {TOOL_GROUPS.map((group, idx) => (
          <div key={idx} className="flex flex-col gap-1">
            {group.map((tool) => {
              const showPopover =
                tool.id === activeTool &&
                (tool.id === 'sticky' || tool.id === 'sticky-wide');
              const button = (
                <ToolButton
                  tool={tool}
                  active={tool.id === activeTool}
                  onClick={() => !tool.disabled && onSelectTool(tool.id)}
                  onStartStickyDrag={onStartStickyDrag}
                />
              );
              if (!showPopover) return <div key={tool.id}>{button}</div>;
              return (
                <div key={tool.id} className="relative">
                  {button}
                  <StickyColorPopover
                    variant={stickyVariant}
                    currentColor={stickyColor}
                    onPickColor={onPickStickyColor}
                    onStartDrag={onStartStickyDrag}
                  />
                </div>
              );
            })}
            {idx < TOOL_GROUPS.length - 1 && <Divider />}
          </div>
        ))}
      </div>
      <div>
        <SettingsButton onOpenSettings={onOpenSettings} />
      </div>
    </aside>
  );
}

function Divider() {
  return <div role="separator" className="self-center w-6 h-px bg-border-subtle my-1.5" />;
}

interface ToolButtonProps {
  tool: ToolDef;
  active: boolean;
  onClick: () => void;
  onStartStickyDrag?: (
    variant: 'square' | 'wide',
    color: StickyColor | undefined,
    screenPos: { x: number; y: number },
  ) => void;
}

function ToolButton({ tool, active, onClick, onStartStickyDrag }: ToolButtonProps) {
  const Icon = tool.icon;
  const title = tool.shortcut ? `${tool.name}  ${tool.shortcut}` : tool.name;
  // Drag-vs-click discriminator. A pointerdown starts tracking; movement past
  // DRAG_THRESHOLD_PX promotes the gesture to a drag (and suppresses the
  // pending click). pointerup with no movement fires the click as usual.
  const draggedRef = useRef(false);
  const handlePointerDown = tool.draggable && onStartStickyDrag
    ? (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        const startX = e.clientX;
        const startY = e.clientY;
        draggedRef.current = false;
        const variant = tool.draggable === 'sticky-wide' ? 'wide' : 'square';
        const onMove = (m: PointerEvent) => {
          const dx = m.clientX - startX;
          const dy = m.clientY - startY;
          if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
          draggedRef.current = true;
          cleanup();
          // No color argument from the rail icon — caller picks the
          // currently armed color (or rotation default) on drop.
          onStartStickyDrag(variant, undefined, { x: m.clientX, y: m.clientY });
        };
        const onUp = () => cleanup();
        const cleanup = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      }
    : undefined;
  const handleClick = () => {
    // Suppress the click that the browser dispatches at the end of a drag —
    // otherwise dragging the sticky button would also arm the tool.
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    onClick();
  };
  return (
    <button
      type="button"
      disabled={tool.disabled}
      title={title}
      aria-label={tool.name}
      aria-pressed={active}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      className={[
        'relative w-9 h-9 inline-flex items-center justify-center rounded-2',
        'transition-colors duration-micro ease-out',
        'focus-visible:outline-none focus-visible:shadow-focus',
        active
          ? 'bg-bg-active text-fg-primary'
          : 'text-fg-secondary hover:bg-bg-hover hover:text-fg-primary',
        tool.disabled
          ? 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-fg-secondary'
          : '',
        tool.draggable ? 'cursor-grab active:cursor-grabbing' : '',
      ].join(' ')}
    >
      {active && (
        <span
          aria-hidden
          className="absolute left-0 top-2 bottom-2 w-0.5 rounded-1 bg-accent"
        />
      )}
      <Icon size={18} strokeWidth={1.5} />
    </button>
  );
}

function SettingsButton({
  onOpenSettings,
}: {
  onOpenSettings?: (anchor: DOMRect) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const disabled = !onOpenSettings;
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      title="Settings"
      aria-label="Settings"
      onClick={() => {
        if (!onOpenSettings || !ref.current) return;
        onOpenSettings(ref.current.getBoundingClientRect());
      }}
      className={[
        'w-9 h-9 inline-flex items-center justify-center rounded-2',
        'transition-colors duration-micro ease-out',
        'focus-visible:outline-none focus-visible:shadow-focus',
        disabled
          ? 'text-fg-secondary opacity-40 cursor-not-allowed'
          : 'text-fg-secondary hover:bg-bg-hover hover:text-fg-primary',
      ].join(' ')}
    >
      <Settings size={18} strokeWidth={1.5} />
    </button>
  );
}
